import { useCallback, useEffect, useRef, useState } from "react"
import { StrikeZoneOverlay } from "../Components/StrikeZoneOverlay";
import { PopUpImage, PopUpInput } from "../Components/PopUp";
import { HLSPlayer } from "../Components/HLSPlayer";
import { useLocation, useNavigate, useParams } from "react-router";
import { YouTubePlayer } from "../Components/YouTubePlayer";
import { Link } from "react-router-dom";
import Tutorial from '../images/tutorial.png'
import Logo from '../images/logo.svg'
import { HLSURLPlayer } from "../Components/HLSURLPlayer";
import { ACCESS_PAGE_MAP, AccountAccessRight, PAGE_NAME_MAP } from "../Components/AccountAccess";
import { NASPlayer } from "../Components/NASPlayer";
import { usePitchSubmit, formatVideoTime } from "../hooks/usePitchSubmit";
import { useHotkeys } from "../hooks/useHotkeys";
import { useStrikeZone } from "../hooks/useStrikeZone";
import { Timeline } from "../Components/Timeline";
import { SettingsDrawer } from "../Components/SettingsDrawer";
import { usePlayback } from "../hooks/usePlayback";
import { TransportBar, STEP_OPTIONS } from "../Components/TransportBar";
import { usePitchLog } from "../hooks/usePitchLog";
import { useGameState } from "../hooks/useGameState";
import { useLeagues } from "../hooks/useLeagues";
import { GameSelector } from "../Components/GameSelector";
import { HudRail } from "../Components/HudRail";
import { HudStrip } from "../Components/HudStrip";
import socket, { SOCKET_URL, SOCKET_TEST_URL } from "../socket";
import { recordReturnPath } from "../authNavigation";
import { requestServerLogout } from "../logoutSession";

// mode → player component lookup, replacing a nested ternary. All four
// players receive the same prop set; HLS and NAS use the shared game selection.
const PLAYER_BY_MODE = {
  youtube: YouTubePlayer,
  hls: HLSPlayer,
  "hls-url": HLSURLPlayer,
  nas: NASPlayer,
}
const CONTROLS_COLLAPSED_KEY = "recording-controls-collapsed:v1"

const StreamRecords = ({test=false, checker=false}) => {
  const playerRef = useRef(null)
  const recordingActive = useRef(true)
  useEffect(() => { recordingActive.current = true; return () => { recordingActive.current = false } }, [])
  const pageRef = useRef(null)
  const workspaceRef = useRef(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [sourceTarget, setSourceTarget] = useState(null)
  const [youtubeKind, setYouTubeKind] = useState("vod")
  const [railCollapsed, setRailCollapsed] = useState(() => {
    const saved = localStorage.getItem("recording-rail-collapsed")
    return saved === null ? window.innerWidth < 1280 || window.innerHeight <= 700 : saved === "true"
  })
  const toggleRail = () => setRailCollapsed((old) => {
    localStorage.setItem("recording-rail-collapsed", String(!old))
    return !old
  })
  const focusWorkspace = useCallback(() => workspaceRef.current?.focus({ preventScroll: true }), [])
  const [controlsCollapsed, setControlsCollapsed] = useState(() => {
    try { return localStorage.getItem(CONTROLS_COLLAPSED_KEY) === "true" }
    catch { return false }
  })
  const toggleControls = (event) => {
    const collapsed = !controlsCollapsed
    setControlsCollapsed(collapsed)
    try { localStorage.setItem(CONTROLS_COLLAPSED_KEY, String(collapsed)) } catch { /* Storage may be unavailable. */ }
    if (event.detail > 0) focusWorkspace()
  }
  const [x, setX] = useState(null);
  const [y, setY] = useState(null);
  const zone = useStrikeZone();
  const [hidden, setHidden] = useState(true)
  const [thidden, setThidden] = useState(true)
  const [startSecond, setStartSecond] = useState(null)
  const [endSecond, setEndSecond] = useState(null)
  const [code, setCode] = useState("")
  const { mode } = useParams()
  const playback = usePlayback(playerRef, mode === "youtube" ? `youtube-${youtubeKind}` : "file")
  const { playing, played, timeline, togglePlay, seekBy, stepFrame, seekToSeconds, onProgress, syncTimeline, reset: resetPlayback } = playback
  const location = useLocation()
  const navigate = useNavigate()
  // Coarse seek step (seconds), changed from the TransportBar and persisted.
  const [coarseStepSec, setCoarseStepSec] = useState(() => {
    const saved = Number(localStorage.getItem("coarseStepSec"))
    return STEP_OPTIONS.includes(saved) ? saved : 3
  })
  const changeCoarseStep = (s) => {
    setCoarseStepSec(s)
    localStorage.setItem("coarseStepSec", String(s))
  }
  const [playbackRate, setPlaybackRate] = useState(1)
  const [volume, setVolume] = useState(1)
  const [connected, setConnected] = useState(socket?.connected ?? false)
  const [matched, setMatched] = useState(localStorage.getItem("matched") === "true")
  const [lastAttempt, setLastAttempt] = useState(null)
  // Ephemeral "已記錄牽制" badge shown for a few seconds after a pickoff send —
  // recorder's hands stay on the keyboard, so this is the only feedback.
  const [pickoffFeedback, setPickoffFeedback] = useState(null)
  const pickoffTimerRef = useRef(null)
  useEffect(() => () => { if (pickoffTimerRef.current) clearTimeout(pickoffTimerRef.current) }, [])
  // Lock the strike-zone box (hotkey L): freezes move/resize and hides the
  // drag grip + resize handle so they can't be mis-grabbed while recording.
  const [locked, setLocked] = useState(false)
  // In fullscreen we drop the setup chrome (header + 賽事 bar) and the right HUD
  // rail to give the video the whole screen; the game state moves to a compact
  // top strip instead. Driven by the browser fullscreenchange event so it stays
  // correct whether toggled by the ⛶ button or by Esc.
  const [isFullscreen, setIsFullscreen] = useState(false)
  // The selected game ({ game_no, league_id, year }) — declared in the 賽事
  // selector, decoupled from the video source so the HUD works in every mode.
  // `game` is the (possibly partial) draft; only a COMPLETE triple is fetched.
  const [game, setGame] = useState(null)
  const leagues = useLeagues()
  // Only a COMPLETE, non-blank triple is fetched; send trimmed values so stray
  // whitespace doesn't miss the exact game_No/year match.
  const activeGame =
    game && game.year?.trim() && game.league_id && game.game_no?.trim()
      ? { year: game.year.trim(), league_id: game.league_id, game_no: game.game_no.trim() }
      : null
  const log = usePitchLog()
  const gameState = useGameState({ game: activeGame })
  useHotkeys({
    playerRef,
    coarseStepSec,
    onSeekBy: seekBy,
    onStepFrame: stepFrame,
    onMoveBox: (dx, dy) => { if (!locked) zone.move(dx, dy) },
    onResizeBox: (delta) => { if (!locked) zone.resize(delta) },
    onToggleLock: () => setLocked((v) => !v),
    onTogglePlay: togglePlay,
    onMarkStart: () => {
      const p = playerRef.current
      if (!p) return
      setStartSecond(p.getCurrentTime())
      setEndSecond(null)
    },
    onMarkEnd: () => {
      const p = playerRef.current
      if (p) setEndSecond(p.getCurrentTime())
    },
    onPickoff: () => handleDataOnSubmit(-99, -99),
    enabled: thidden && (checker || hidden),
  })
  const submitPitch = usePitchSubmit({
    socket,
    playerRef,
    onUnmatched: (payload) => {
      if (!recordingActive.current) return
      setHidden(false)
      localStorage.setItem("matched", false)
      setMatched(false)
      // skip the pickoff sentinel; keep last + status referring to the same event
      if (payload && payload.KZoneY !== -144) setLastAttempt({ payload, status: "failed" })
    },
    onSent: (payload) => {
      if (!recordingActive.current) return
      setMatched(true)
      localStorage.setItem("matched", "true")
      if (payload.KZoneY !== -144) {
        setLastAttempt({ payload, status: "sent" })
        log.record(payload)
        gameState.refresh() // a real pitch changed the state — pull fresh B-S-O/counts
      } else {
        // Pickoff sentinel sent OK — flash a short-lived badge, then clear it.
        if (pickoffTimerRef.current) clearTimeout(pickoffTimerRef.current)
        setPickoffFeedback(payload.VideoTime)
        pickoffTimerRef.current = setTimeout(() => setPickoffFeedback(null), 3500)
      }
    },
    onError: () => { if (recordingActive.current) alert("未配對記錄軟體/操作錯誤/未連結影片") },
  })
  const handleDataOnSubmit = (x, y) => { if (recordingActive.current) submitPitch(x, y) }
  // Fullscreen the whole cockpit (not the raw <video>) so the strike-zone
  // overlay, HUD and controls stay visible/usable — and toggle on/off.
  const onFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.()
    } else {
      pageRef.current?.requestFullscreen?.()
    }
  }
  useEffect(() => {
    // Only OUR page going fullscreen should reflow the cockpit — a player going
    // fullscreen on its own (native video chrome) must not flip the page UI.
    const onChange = () => setIsFullscreen(document.fullscreenElement === pageRef.current)
    document.addEventListener("fullscreenchange", onChange)
    return () => document.removeEventListener("fullscreenchange", onChange)
  }, []);
  useEffect(() => {
    const stage = pageRef.current.querySelector(".cockpit-stage");
    const videoArea = pageRef.current.querySelector(".video-area");
    const resize = () => {
      const available = videoArea.getBoundingClientRect();
      const desktop = isFullscreen || window.innerWidth >= 1024;
      const width = desktop ? Math.min(available.width, available.height * 16 / 9) : available.width;
      stage.style.setProperty("--video-fit-width", `${Math.max(0, width)}px`);
    };
    resize();
    const observer = window.ResizeObserver ? new window.ResizeObserver(resize) : null;
    observer?.observe(videoArea);
    window.addEventListener("resize", resize);
    return () => { observer?.disconnect(); window.removeEventListener("resize", resize); };
  }, [isFullscreen, railCollapsed, controlsCollapsed]);
  useEffect(() => {
    // Routes reuse this component, so changing environments must reconnect and
    // require a new pairing. Ordinary reconnects to the same endpoint keep their
    // existing queue; only an environment switch discards pending work.
    const targetUri = test ? SOCKET_TEST_URL : SOCKET_URL
    if (socket.io.uri !== targetUri) {
      socket.disconnect()
      // Socket.IO 2.3.0 keeps buffers/acks after disconnect. Detach the old
      // engine before reconnecting, including a close still waiting for drain.
      socket.io.cleanup()
      socket.sendBuffer = []
      socket.receiveBuffer = []
      socket.acks = {}
      localStorage.setItem("matched", "false")
      setMatched(false)
      setCode("")
      setHidden(false)
      setLastAttempt(null)
      setPickoffFeedback(null)
      if (pickoffTimerRef.current) clearTimeout(pickoffTimerRef.current)
      socket.io.uri = targetUri
      socket.connect()
    } else if (!socket.connected) {
      // Explicit logout disconnects the singleton; a new login must reopen it.
      socket.connect()
    }
  }, [test]);
  // A newly accepted source, mode or explicit retry starts a fresh timeline.
  // Editing the source/game draft alone leaves the playing clip untouched.
  const resetVideo = useCallback(() => {
    resetPlayback()
    setStartSecond(null)
    setEndSecond(null)
    // NOTE: `game` (the 賽事 selection) intentionally persists across source
    // switches — the recorder is still recording the same game.
  }, [resetPlayback]);
  useEffect(() => {
    const onConn = () => setConnected(true)
    const onDisc = () => setConnected(false)
    socket.on("connect", onConn)
    socket.on("disconnect", onDisc)
    setConnected(!!socket.connected)
    return () => {
      socket.off("connect", onConn)
      socket.off("disconnect", onDisc)
    }
    // `socket` is now a module-level singleton import, not a prop — it's a
    // stable reference, so it isn't a real effect dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ['SI000014','SI000009'].includes(JSON.parse(localStorage.getItem('stats-insight-login-info')).id)
  
  const navigateToAnotherPage = (e) => {
    let slice_index = location.pathname.slice(1,).indexOf('/')
    const rootURL = location.pathname.slice(0, slice_index+1)
    navigate(`${rootURL}/${e.target.value}`)
  }

  const logout = () => {
    recordingActive.current = false
    playerRef.current = null
    socket.disconnect()
    socket.io.cleanup()
    socket.sendBuffer = []
    socket.receiveBuffer = []
    socket.acks = {}
    localStorage.removeItem("stats-insight-login")
    localStorage.removeItem("stats-insight-login-info")
    localStorage.removeItem("matched")
    requestServerLogout()
    navigate("/login", { replace: true, state: { returnTo: recordReturnPath(location.pathname) } })
  }

  const reauthenticate = () => {
    localStorage.removeItem("stats-insight-login")
    localStorage.removeItem("stats-insight-login-info")
    navigate("/login", { replace: true, state: { returnTo: recordReturnPath(location.pathname) } })
  }

  const status = !connected ? "disconnected" : matched ? "connected" : "unmatched"
  const videoTime = formatVideoTime(played)
  // Rendered INSIDE the player's .video-box so it overlays (and scales with)
  // the responsive video, instead of floating in page coordinates.
  const overlay = (
    <StrikeZoneOverlay box={zone.box} x={x} y={y} setX={setX} setY={setY} onSubmit={handleDataOnSubmit} setPosition={zone.setPosition} setSize={zone.setSize} locked={locked} />
  )
  const PlayerComponent = PLAYER_BY_MODE[mode] || NASPlayer
  // Unknown/未登記 user id → AccountAccessRight[id] is undefined, which used to
  // crash ACCESS_PAGE_MAP[undefined].map(...) into a white screen. Fall back to
  // an empty option list and surface a message instead.
  let loginInfo = null
  try { loginInfo = JSON.parse(localStorage.getItem('stats-insight-login-info')) } catch (e) { loginInfo = null }
  const accessRole = loginInfo && AccountAccessRight[loginInfo.id]
  const pageOptions = ACCESS_PAGE_MAP[accessRole] || []

  return (
    <div className={`page recording-workspace${isFullscreen ? " is-fullscreen" : ""}${railCollapsed ? " rail-collapsed" : ""}`} ref={pageRef}>
      <PopUpImage hidden={thidden} setHidden={setThidden} image={Tutorial} />
      {checker? []:<PopUpInput code={code} setCode={setCode} socket={socket} hidden={hidden} setHidden={setHidden} onMatched={setMatched} />}
      {isFullscreen && <HudStrip status={status} videoTime={videoTime} gameState={gameState.state} gameStateError={gameState.error} x={x} y={y} startSecond={startSecond} endSecond={endSecond} pickoffFeedback={pickoffFeedback} />}
      <div className="function_btns">
        <div className="workspace-brand"><img className="brand-logo" src={Logo} alt="StatsInsight" /><span>影片記錄工作台</span></div>
        <button type="button" className="settings-toggle" onClick={() => setSettingsOpen(true)}>賽事與影片設定</button>
        <button className="record-linker" onClick={() => setHidden(false)}>連線記錄軟體</button>
        <button className="pickoff" onClick={() => handleDataOnSubmit(-99, -99)}>紀錄牽制時間點 (P)</button>
        <button className="tutorial" onClick={() => setThidden(false)}>查看功能說明</button>
        <button type="button" className="logout-button" onClick={logout}>登出</button>
      </div>
      <div className="workspace-summary">
        <span className="source-summary">{PAGE_NAME_MAP[mode] || "NAS"} · {activeGame ? `${activeGame.year} / ${activeGame.league_id} / G${activeGame.game_no}` : "尚未選擇賽事"}</span>
        {(railCollapsed && !isFullscreen) && <span className={`compact-status ${status}`}>{status === "connected" ? "已連線記錄軟體" : status === "unmatched" ? "未配對記錄軟體" : "連線中斷"}{status === "disconnected" && <button type="button" onClick={() => socket.connect()}>重新連線</button>}</span>}
        <span className="workspace-feedback" role="status">{lastAttempt?.status === "failed" ? "✗ 送出失敗，請確認配對與連線" : pickoffFeedback ? `已記錄牽制 @ ${pickoffFeedback}` : "空白鍵 播放／暫停 · ← → 快轉 · , . 逐格"}</span>
        <button type="button" className="controls-toggle" aria-expanded={!controlsCollapsed} aria-controls="recording-transport-controls" onClick={toggleControls}>
          {controlsCollapsed ? "展開播放控制列" : "收合播放控制列"}
        </button>
      </div>
      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <label className="video-source">
          影片來源
          {pageOptions.length > 0 ? (
            <select value={mode} onChange={navigateToAnotherPage}>
              { pageOptions.map((e) => <option key={e} value={e}>{PAGE_NAME_MAP[e]}</option>) }
            </select>
          ) : (
            <span className="access-denied-hint" style={{ color: "#f87171" }}>此帳號無存取權限</span>
          )}
        </label>
      <div className="game-bar">
        <GameSelector leagues={leagues.leagues} game={game} onChange={setGame} />
        {leagues.error ? (
          <span className="game-bar-hint league-error" style={{ color: "#f87171" }}>
            {leagues.authError ? "登入已失效，請重新登入以載入聯賽" : "聯賽清單載入失敗"}
            {leagues.authError
              ? <button type="button" onClick={reauthenticate}>重新登入</button>
              : <button type="button" onClick={leagues.refresh}>重試</button>}
          </span>
        ) : (
          <span className="game-bar-hint">{activeGame ? "賽事現況已連動 →" : "選擇賽事以顯示現況（任何影片來源皆可）"}</span>
        )}
      </div>
        <div className="source-settings" ref={setSourceTarget} />
        <Link className="mode-switcher" to={test ? "/intime-record/hls" : "/intime-record-test/hls"}>切換為{test ? "一般模式" : "測試模式"}</Link>
      </SettingsDrawer>
      <div className="cockpit-main">
        <div className="cockpit-stage" ref={workspaceRef} tabIndex={-1} aria-label="影片工作區">
          <div className="video-area">
            <PlayerComponent playerRef={playerRef} playing={playing} onProgress={onProgress} onDuration={syncTimeline} onSourceChange={resetVideo}
              onPlaybackChange={playback.playbackChanged} onSeek={playback.seeked} onFailure={playback.failed}
              playbackRate={playbackRate} volume={volume} game={game} sourceTarget={sourceTarget}
              youtubeKind={youtubeKind} onYouTubeKindChange={setYouTubeKind}>{overlay}</PlayerComponent>
          </div>
          <Timeline timeline={timeline} played={played} sourceVersion={`${mode}:${youtubeKind}:${playback.sourceVersion}`} onSeek={seekToSeconds} onStep={seekBy} coarseStepSec={coarseStepSec}
            onGoLive={playback.goLive} onPointerComplete={focusWorkspace} startSecond={startSecond} endSecond={endSecond} />
        </div>
        <div className="hud-shell">
          <button type="button" className="rail-toggle" aria-label={railCollapsed ? "展開記錄面板" : "收合記錄面板"} aria-expanded={!railCollapsed} onClick={toggleRail}>{railCollapsed ? "‹" : "›"}<span>{railCollapsed ? "" : "記錄面板"}</span></button>
        <HudRail status={status} videoTime={videoTime} count={log.count} last={lastAttempt?.payload || null} lastStatus={lastAttempt?.status || "sent"} recent={log.recent} gameState={gameState.state} gameStateError={gameState.error} onRefreshGameState={gameState.refresh} x={x} y={y} startSecond={startSecond} endSecond={endSecond} onReconnect={() => socket.connect()} pickoffFeedback={pickoffFeedback} />
        </div>
      </div>
      <div id="recording-transport-controls" className="transport-controls" hidden={controlsCollapsed}>
      {!controlsCollapsed && <TransportBar
        playing={playing}
        onTogglePlay={togglePlay}
        coarseStepSec={coarseStepSec}
        onCoarseStepChange={changeCoarseStep}
        onStep={seekBy}
        onFrame={stepFrame}
        onPointerComplete={focusWorkspace}
        playbackRate={playbackRate}
        onRateChange={setPlaybackRate}
        volume={volume}
        onVolumeChange={setVolume}
        onFullscreen={onFullscreen}
      />}
      </div>
    </div>
  )
}

export default StreamRecords
